/* eslint-disable */
"use client";

import { Link } from "./router";
import { useRouter } from "./router";
import { useState, useTransition } from "react";
import {
  ArrowLeft,
  CalendarDays,
  Download,
  FileSpreadsheet,
  Loader2,
  LockKeyhole,
  ReceiptText,
  Search,
  ShieldCheck,
  TriangleAlert,
  X,
} from "lucide-react";
import type { employees, holidays, payrollPeriods, payrollRecords } from "./types";
import {
  Button,
  Card,
  EmptyState,
  EmployeeTypeBadge,
  Field,
  Modal,
  Pill,
  Select,
  StatusPill,
  TextInput,
} from "./ui";
import { useUrlFilters } from "./router";
import { voidPayrollAction } from "./api";
import { activeGovColumnsForCutoff, holidayTypeLabel, resolveCutoffMode } from "./payroll-shared";
import { cn, formatDate, formatPeso } from "./utils";
import { RollingNumber } from "./rolling-number";

type PeriodRow = typeof payrollPeriods.$inferSelect;
type HolidayRow = typeof holidays.$inferSelect;
type Row = { record: typeof payrollRecords.$inferSelect; employee: typeof employees.$inferSelect };

/* --------------------------------- void modal -------------------------------- */
function VoidModal({ period, onClose }: { period: PeriodRow; onClose: () => void }) {
  const router = useRouter();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [pending, startTransition] = useTransition();

  const submit = () => {
    setError("");
    startTransition(() => {
      void (async () => {
        const res = await voidPayrollAction(period.id, pin, period.ledger || "payroll");
        if (!res.ok) {
          setError(res.error);
          return;
        }
        router.refresh();
        onClose();
      })();
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Void this payroll run?"
      subtitle="Net pay records will be removed and every loan deduction collected in this run will be refunded to loan balances. The period is kept as an audit trail marked \u201cVoided\u201d."
      icon={<TriangleAlert className="h-5 w-5" />}
    >
      <div className="rounded-xl border border-[color-mix(in_oklab,var(--danger)_30%,transparent)] bg-[color-mix(in_oklab,var(--danger)_8%,transparent)] px-4 py-3">
        <p className="text-xs leading-relaxed text-muted">
          <span className="font-bold text-danger">{period.name}</span> &#183; {formatDate(period.startDate)} &mdash;{" "}
          {formatDate(period.endDate)} &#183; {formatPeso(Number(period.totalNet))} net
        </p>
      </div>
      <Field label="Admin PIN" hint="set in Preferences" className="mt-4">
        <TextInput
          type="password"
          inputMode="numeric"
          placeholder="Enter admin PIN to unlock"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          autoFocus
        />
      </Field>
      {error && (
        <p className="mt-4 rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3.5 py-2.5 text-xs font-semibold text-danger">
          {error}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="ghost" onClick={onClose} disabled={pending}>Keep record</Button>
        <Button variant="danger" onClick={submit} disabled={pending || !pin}>
          {pending && <Loader2 className="h-4 w-4 animate-spin" />} Void payroll run
        </Button>
      </div>
    </Modal>
  );
}

/* -------------------------------- payslip modal -------------------------------- */
function PayslipModal({ row, period, onClose }: { row: Row; period: PeriodRow; onClose: () => void }) {
  const { record, employee } = row;
  const cutoffMode = resolveCutoffMode(period.startDate, period.endDate);
  const activeGovColumns = activeGovColumnsForCutoff(cutoffMode);

  const lines: Array<[string, string, "add" | "sub" | "plain"]> = [
    [`Regular days (${record.paidDays} paid days)`, formatPeso(Number(record.regularPay)), "add"],
    ["Holiday premium pay", formatPeso(Number(record.holidayPay)), "add"],
  ];

  for (const col of activeGovColumns) {
    let val = 0;
    if (col.key === "hdmf") val = Number(record.hdmfDeduction);
    else if (col.key === "philhealth") val = Number(record.philhealthDeduction);
    else if (col.key === "sss") val = Number(record.sssDeduction);
    else if (col.key === "withholdingTax") val = Number(record.taxDeduction);

    lines.push([`${col.label} contribution`, `\u2212${formatPeso(val)}`, "sub"]);
  }

  lines.push(["Loan amortization", `\u2212${formatPeso(Number(record.loanDeduction))}`, "sub"]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${employee.firstName} ${employee.lastName}`}
      subtitle={`${employee.employeeCode} \u00b7 ${period.name} \u00b7 ${formatDate(period.startDate)} \u2014 ${formatDate(period.endDate)}`}
      icon={<ReceiptText className="h-5 w-5" />}
    >
      <div className="mb-4 flex items-center justify-between rounded-xl bg-surface2 px-4 py-3">
        <div className="text-xs font-semibold text-muted">
          <p>{employee.position || "\u2014"}</p>
          <p className="mt-0.5 text-faint">{employee.department || "General"}</p>
        </div>
        <EmployeeTypeBadge type={employee.employeeType} />
      </div>
      <div className="space-y-1">
        {lines.map(([label, value, kind]) => (
          <div key={label} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm odd:bg-surface2">
            <span className="text-muted">{label}</span>
            <span
              className={cn(
                "font-semibold",
                kind === "add" ? "text-success" : kind === "sub" ? "text-danger" : "text-text"
              )}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-xl border border-[color-mix(in_oklab,var(--accent)_30%,transparent)] bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] px-4 py-3.5">
        <div>
          <p className="text-[10px] font-bold tracking-[0.16em] text-accent-strong uppercase">Net pay</p>
          <p className="text-[11px] text-muted">Gross {formatPeso(Number(record.grossPay))}</p>
        </div>
        <p className="font-display text-2xl font-bold text-accent">{formatPeso(Number(record.netPay))}</p>
      </div>
    </Modal>
  );
}

/* --------------------------------- detail page -------------------------------- */
export function PayrollDetailClient({
  period,
  holidays: holidayRows,
  rows,
  departments,
  basePath,
  backLabel,
  initialFilters,
}: {
  period: PeriodRow;
  holidays: HolidayRow[];
  rows: Row[];
  departments: string[];
  basePath: string;
  backLabel: string;
  initialFilters: { q: string; type: string; dept: string };
}) {
  const { values, set, reset } = useUrlFilters(initialFilters);
  const [voidOpen, setVoidOpen] = useState(false);
  const [payslip, setPayslip] = useState<Row | null>(null);
  const hasFilters = values.q || values.type || values.dept;
  const finalized = period.status === "finalized";
  const cutoffMode = resolveCutoffMode(period.startDate, period.endDate);
  const activeGovColumns = activeGovColumnsForCutoff(cutoffMode);

  const filteredTotals = rows.reduce(
    (acc, { record }) => ({
      gross: acc.gross + Number(record.grossPay),
      hdmf: acc.hdmf + Number(record.hdmfDeduction),
      philhealth: acc.philhealth + Number(record.philhealthDeduction),
      sss: acc.sss + Number(record.sssDeduction),
      tax: acc.tax + Number(record.taxDeduction),
      loan: acc.loan + Number(record.loanDeduction),
      net: acc.net + Number(record.netPay),
    }),
    { gross: 0, hdmf: 0, philhealth: 0, sss: 0, tax: 0, loan: 0, net: 0 }
  );

  const exportCsv = () => {
    const govHeader = activeGovColumns.map((c) => c.label);
    const header = [
      "Employee Code",
      "Name",
      "Type",
      "Department",
      "Position",
      "Paid Days",
      "Regular Pay",
      "Holiday Pay",
      "Gross Pay",
      ...govHeader,
      "Loan Deduction",
      "Total Deductions",
      "Net Pay",
    ];
    const body = rows.map(({ record, employee }) => {
      const govValues = activeGovColumns.map((c) => {
        if (c.key === "hdmf") return record.hdmfDeduction;
        if (c.key === "philhealth") return record.philhealthDeduction;
        if (c.key === "sss") return record.sssDeduction;
        if (c.key === "withholdingTax") return record.taxDeduction;
        return "0.00";
      });
      return [
        employee.employeeCode,
        `${employee.firstName} ${employee.lastName}`,
        employee.employeeType,
        employee.department,
        employee.position,
        record.paidDays,
        record.regularPay,
        record.holidayPay,
        record.grossPay,
        ...govValues,
        record.loanDeduction,
        record.totalDeductions,
        record.netPay,
      ];
    });
    const csv = [header, ...body]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payora-${period.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="rise-in mb-6">
        <Link
          href={basePath}
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold text-muted transition-colors hover:text-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to {backLabel}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-2xl font-bold tracking-tight text-text sm:text-3xl">{period.name}</h1>
              <StatusPill status={period.status} />
              {finalized && (
                <Pill tone="blue">
                  <LockKeyhole className="h-3 w-3" /> Secured
                </Pill>
              )}
            </div>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4 text-faint" />
                {formatDate(period.startDate)} &mdash; {formatDate(period.endDate)}
              </span>
              <span>{period.workingDays} working days</span>
              <span>{period.employeeCount} employees</span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-faint" />
                Locked {formatDate(String(period.createdAt).slice(0, 10))}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Button variant="outline" onClick={exportCsv} disabled={!rows.length}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            {finalized && (
              <Button variant="danger" onClick={() => setVoidOpen(true)}>
                <TriangleAlert className="h-4 w-4" /> Void run
              </Button>
            )}
          </div>
        </div>
      </div>

      {!finalized && (
        <Card className="rise-in mb-4 border-[color-mix(in_oklab,var(--danger)_30%,transparent)] bg-[color-mix(in_oklab,var(--danger)_7%,transparent)] p-4 ring-1 ring-inset ring-[color-mix(in_oklab,var(--danger)_30%,transparent)]">
          <p className="text-sm font-semibold text-danger">This payroll run has been voided.</p>
          <p className="mt-0.5 text-xs text-muted">
            Its records were removed and loan deductions were refunded. The entry remains as an audit trail.
          </p>
        </Card>
      )}

      {finalized && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 xl:grid-cols-4">
            {[
              { label: "Gross pay", value: formatPeso(Number(period.totalGross)), tone: "text-text" },
              { label: "Total deductions", value: `\u2212${formatPeso(Number(period.totalDeductions))}`, tone: "text-danger" },
              { label: "Net pay", value: formatPeso(Number(period.totalNet)), tone: "text-accent" },
              {
                label: "Avg. net / employee",
                value: formatPeso(period.employeeCount ? Number(period.totalNet) / period.employeeCount : 0),
                tone: "text-text",
              },
            ].map((s) => (
              <Card key={s.label} className="rise-in p-5">
                <p className="text-[11px] font-bold tracking-[0.14em] text-faint uppercase">{s.label}</p>
                <RollingNumber text={s.value} className={cn("font-display mt-2 text-xl font-bold tracking-tight sm:text-2xl", s.tone)} />
              </Card>
            ))}
          </div>

          {holidayRows.length > 0 && (
            <Card className="rise-in mb-4 p-4 sm:px-5">
              <p className="mb-2.5 text-[11px] font-bold tracking-[0.14em] text-faint uppercase">
                Holiday rules applied
              </p>
              <div className="flex flex-wrap gap-2">
                {holidayRows.map((h) => (
                  <Pill key={h.id} tone={Number(h.multiplier) === 0 ? "gray" : Number(h.multiplier) >= 2 ? "yellow" : "blue"}>
                    {h.name} &#183; {formatDate(h.holidayDate)} &#183; &times;{Number(h.multiplier).toFixed(1)}
                    <span className="opacity-70">{holidayTypeLabel(h.type, h.worked === "yes")}</span>
                  </Pill>
                ))}
              </div>
            </Card>
          )}

          <Card className="rise-in mb-4 p-3.5">
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-[1fr_160px_190px_auto]">
              <div className="relative col-span-2 md:col-span-1">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
                <TextInput
                  className="pl-9"
                  placeholder="Search employee\u2026"
                  value={values.q}
                  onChange={(e) => set("q", e.target.value)}
                />
              </div>
              <Select value={values.type} onChange={(e) => set("type", e.target.value)}>
                <option value="">All types</option>
                <option value="313">Type 313</option>
                <option value="261">Type 261</option>
              </Select>
              <Select value={values.dept} onChange={(e) => set("dept", e.target.value)}>
                <option value="">All departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Select>
              {hasFilters ? (
                <Button variant="ghost" onClick={reset}>
                  <X className="h-4 w-4" /> Clear
                </Button>
              ) : (
                <div className="hidden items-center px-2 text-[11px] font-semibold text-faint md:flex">{rows.length} records</div>
              )}
            </div>
          </Card>

          <Card className="rise-in overflow-hidden">
            {rows.length === 0 ? (
              <EmptyState
                icon={<FileSpreadsheet className="h-6 w-6" />}
                title="No records match"
                description="Adjust the filters to find the employee records you need."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1080px] text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface2 text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                      <th className="px-5 py-3 sm:px-6">Employee</th>
                      <th className="px-4 py-3 text-right">Paid days</th>
                      <th className="px-4 py-3 text-right">Regular</th>
                      <th className="px-4 py-3 text-right">Holiday</th>
                      <th className="px-4 py-3 text-right">Gross</th>
                      {activeGovColumns.map((col) => (
                        <th key={col.key} className="px-4 py-3 text-right">
                          {col.label}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right">Loan</th>
                      <th className="px-5 py-3 text-right sm:px-6">Net pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.record.id}
                        onClick={() => setPayslip(row)}
                        className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-surface2/70"
                        title="View payslip"
                      >
                        <td className="px-5 py-3.5 sm:px-6">
                          <p className="font-semibold text-text">
                            {row.employee.firstName} {row.employee.lastName}
                          </p>
                          <p className="mt-0.5 flex items-center gap-2 text-[11px] font-medium text-faint">
                            {row.employee.employeeCode}
                            <EmployeeTypeBadge type={row.employee.employeeType} />
                          </p>
                        </td>
                        <td className="px-4 py-3.5 text-right font-semibold text-muted">{row.record.paidDays}</td>
                        <td className="px-4 py-3.5 text-right font-medium whitespace-nowrap text-muted">
                          {formatPeso(Number(row.record.regularPay), { decimals: 0 })}
                        </td>
                        <td className="px-4 py-3.5 text-right font-medium whitespace-nowrap text-primary-strong">
                          {Number(row.record.holidayPay) > 0 ? `+${formatPeso(Number(row.record.holidayPay), { decimals: 0 })}` : "\u2014"}
                        </td>
                        <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap text-text">
                          {formatPeso(Number(row.record.grossPay))}
                        </td>
                        {activeGovColumns.map((col) => {
                          const val =
                            col.key === "hdmf"
                              ? Number(row.record.hdmfDeduction)
                              : col.key === "philhealth"
                                ? Number(row.record.philhealthDeduction)
                                : col.key === "sss"
                                  ? Number(row.record.sssDeduction)
                                  : Number(row.record.taxDeduction);
                          return (
                            <td key={col.key} className="px-4 py-3.5 text-right font-medium whitespace-nowrap text-danger/90">
                              &minus;{formatPeso(val, { decimals: 0 })}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3.5 text-right font-medium whitespace-nowrap text-danger/90">
                          {Number(row.record.loanDeduction) > 0 ? `\u2212${formatPeso(Number(row.record.loanDeduction), { decimals: 0 })}` : "\u2014"}
                        </td>
                        <td className="px-5 py-3.5 text-right font-display font-bold whitespace-nowrap text-accent sm:px-6">
                          {formatPeso(Number(row.record.netPay))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border bg-surface2 text-[13px] font-bold">
                      <td className="px-5 py-3.5 text-faint uppercase sm:px-6" colSpan={4}>
                        {hasFilters ? "Filtered totals" : "Period totals"} &#183; {rows.length}
                      </td>
                      <td className="px-4 py-3.5 text-right whitespace-nowrap text-text">{formatPeso(filteredTotals.gross, { decimals: 0 })}</td>
                      {activeGovColumns.map((col) => {
                        const val =
                          col.key === "hdmf"
                            ? filteredTotals.hdmf
                            : col.key === "philhealth"
                              ? filteredTotals.philhealth
                              : col.key === "sss"
                                ? filteredTotals.sss
                                : filteredTotals.tax;
                        return (
                          <td key={col.key} className="px-4 py-3.5 text-right whitespace-nowrap text-danger">
                            &minus;{formatPeso(val, { decimals: 0 })}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3.5 text-right whitespace-nowrap text-danger">&minus;{formatPeso(filteredTotals.loan, { decimals: 0 })}</td>
                      <td className="px-5 py-3.5 text-right font-display whitespace-nowrap text-accent sm:px-6">
                        {formatPeso(filteredTotals.net, { decimals: 0 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {!finalized && (
        <Card className="rise-in">
          <EmptyState
            icon={<LockKeyhole className="h-6 w-6" />}
            title="Records removed"
            description="Voided payroll runs keep no payslip data. The audit entry above is retained for accountability."
          />
        </Card>
      )}

      {voidOpen && <VoidModal period={period} onClose={() => setVoidOpen(false)} />}
      {payslip && <PayslipModal row={payslip} period={period} onClose={() => setPayslip(null)} />}
    </>
  );
}
